/**
 * Per-contract custom sections ("Egna sektioner") — see ADR-0060.
 *
 * A user editing a contract may add their own numbered `§` sections beyond
 * the template's fixed, legally-reviewed ones. They are bound to the single
 * contract (not the template): stored as an ordered array under a reserved
 * key in `contract.values` (the same mechanism `__contract_number_auto`
 * uses), and rendered by the `<Section>` atom as a sibling right after the
 * declared section named by `afterRef` — so a custom section can sit between
 * any two existing sections, routed by the stable section `ref` label
 * (ADR-0049) and persisted as that label.
 *
 * Structure (post-redesign 2026-06-02):
 *   Section  → ordered SubSections
 *   SubSection → optional title (named ⇒ numbered §N.M sub-clause;
 *                empty ⇒ "blend": renders as plain section content)
 *              → ordered Components
 *   Component → key→value rows OR free text, rendered in declaration order
 *
 * This module is the single source of truth for the shape + read/normalise
 * helpers, imported by the editor, the numbering atom, and the backend
 * copy-from procedure. Kept zero-dependency (no zod) because the templates
 * package is zod-free; `normalizeCustomSections` is the defensive boundary —
 * `update_draft` stores `values` as opaque JSONB, so shape is enforced on
 * read, not on write. It also migrates the pre-redesign flat shape
 * (`{ kind, rows, body }`) forward.
 */
/** Reserved `contract.values` key holding the ordered custom-section list. */
export const CUSTOM_SECTIONS_KEY = '__custom_sections';
/** Numbering-map / section-ref namespace for a custom section. */
const CUSTOM_REF_PREFIX = 'custom:';
/** Caps — bound JSONB size and keep the editor sane. */
export const MAX_CUSTOM_SECTIONS = 20;
export const MAX_CUSTOM_SUBSECTIONS = 20;
export const MAX_CUSTOM_COMPONENTS = 20;
export const MAX_CUSTOM_ROWS = 50;
export const MAX_CUSTOM_TITLE_LEN = 200;
export const MAX_CUSTOM_KEY_LEN = 200;
export const MAX_CUSTOM_VALUE_LEN = 4000;
export const MAX_CUSTOM_BODY_LEN = 20000;
/** `'custom:<id>'` — the section-ref / numbering-map key for a custom section. */
export const customSectionAnchorRef = (id) => `${CUSTOM_REF_PREFIX}${id}`;
/** True for a ref produced by `customSectionAnchorRef`. */
export const isCustomAnchorRef = (ref) => ref.startsWith(CUSTOM_REF_PREFIX);
/** Extract the custom-section id from a `'custom:<id>'` ref (else null). */
export const customIdFromRef = (ref) => ref.startsWith(CUSTOM_REF_PREFIX) ? ref.slice(CUSTOM_REF_PREFIX.length) : null;
const clamp = (s, max) => typeof s === 'string' ? s.slice(0, max) : '';
// ── Normalisation (defensive read boundary + forward migration) ─────────
function normalizeRows(value) {
    if (!Array.isArray(value))
        return [];
    return value
        .slice(0, MAX_CUSTOM_ROWS)
        .filter((x) => !!x && typeof x === 'object')
        .map((x) => ({
        key: clamp(x.key, MAX_CUSTOM_KEY_LEN),
        value: clamp(x.value, MAX_CUSTOM_VALUE_LEN),
    }));
}
function normalizeComponent(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const id = typeof r.id === 'string' && r.id ? r.id : '';
    if (!id)
        return null;
    if (r.kind === 'text' || r.kind === 'paragraph') {
        return { id, kind: 'text', body: clamp(r.body, MAX_CUSTOM_BODY_LEN) };
    }
    return { id, kind: 'key_value', rows: normalizeRows(r.rows) };
}
function normalizeSubSection(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const id = typeof r.id === 'string' && r.id ? r.id : '';
    if (!id)
        return null;
    const components = Array.isArray(r.components)
        ? r.components
            .slice(0, MAX_CUSTOM_COMPONENTS)
            .map(normalizeComponent)
            .filter((c) => c !== null)
        : [];
    const title = clamp(r.title, MAX_CUSTOM_TITLE_LEN);
    return title ? { id, title, components } : { id, components };
}
/**
 * Migrate a pre-redesign flat section (`{ id, title, kind, rows?, body? }`)
 * into one blended sub-section holding a single component.
 */
function migrateFlatSection(id, title, r) {
    const comp = r.kind === 'paragraph' || r.kind === 'text'
        ? { id: `${id}-c`, kind: 'text', body: clamp(r.body, MAX_CUSTOM_BODY_LEN) }
        : { id: `${id}-c`, kind: 'key_value', rows: normalizeRows(r.rows) };
    return { id, title, subSections: [{ id: `${id}-s`, components: [comp] }] };
}
/**
 * Defensive parse of a raw `values[CUSTOM_SECTIONS_KEY]` into a clean,
 * clamped `CustomSection[]`. Drops malformed entries (missing id) rather
 * than throwing, and migrates the pre-redesign flat shape. Order preserved.
 */
export function normalizeCustomSections(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        if (out.length >= MAX_CUSTOM_SECTIONS)
            break;
        if (!raw || typeof raw !== 'object')
            continue;
        const r = raw;
        const id = typeof r.id === 'string' && r.id ? r.id : '';
        if (!id)
            continue;
        const title = clamp(r.title, MAX_CUSTOM_TITLE_LEN);
        const afterRef = typeof r.afterRef === 'string' && r.afterRef ? r.afterRef : undefined;
        // Forward-migrate the old flat shape (no subSections, has a `kind`).
        if (!Array.isArray(r.subSections) && (r.kind !== undefined || r.rows !== undefined || r.body !== undefined)) {
            const migrated = migrateFlatSection(id, title, r);
            out.push(afterRef ? { ...migrated, afterRef } : migrated);
            continue;
        }
        const subSections = Array.isArray(r.subSections)
            ? r.subSections
                .slice(0, MAX_CUSTOM_SUBSECTIONS)
                .map(normalizeSubSection)
                .filter((s) => s !== null)
            : [];
        out.push(afterRef ? { id, title, afterRef, subSections } : { id, title, subSections });
    }
    return out;
}
// ── Visibility ──────────────────────────────────────────────────────────
const componentHasContent = (c) => c.kind === 'text'
    ? c.body.trim() !== ''
    : c.rows.some((r) => r.key.trim() !== '' || r.value.trim() !== '');
const subSectionHasContent = (ss) => (ss.title ?? '').trim() !== '' || ss.components.some(componentHasContent);
/** Whether a section holds any content worth rendering / numbering. */
export function isCustomSectionVisible(cs) {
    return cs.title.trim() !== '' || cs.subSections.some(subSectionHasContent);
}
/**
 * In `final`/`preview` mode, strip empty components and empty sub-sections so
 * the rendered document carries no blank rows or headings. A named sub-section
 * is kept (its title is content); a blend sub-section survives only if it has
 * at least one non-empty component.
 */
function pruneForRender(cs) {
    const subSections = cs.subSections
        .map((ss) => ({
        ...ss,
        components: ss.components.filter(componentHasContent),
    }))
        .filter((ss) => (ss.title ?? '').trim() !== '' || ss.components.length > 0);
    return { ...cs, subSections };
}
/**
 * Read the contract's custom sections from its `values`. In `mode==='blank'`
 * (authoring / empty-form preview) everything is returned so the user sees
 * the full shape; in `preview`/`final` only sections with content are kept
 * and blank components/sub-sections are pruned (mirrors `SectionDef.visibleWhen`).
 */
export function readCustomSections(values, mode) {
    const all = normalizeCustomSections(values?.[CUSTOM_SECTIONS_KEY]);
    if (mode === 'blank')
        return all;
    return all.filter(isCustomSectionVisible).map(pruneForRender);
}
// ── Placement ───────────────────────────────────────────────────────────
/**
 * Default placement when a custom section has no (or an unknown) `afterRef`:
 * after the second-to-last declared section, i.e. before the closing /
 * signing block — matching the original single-slot behaviour.
 */
export function defaultCustomAfterRef(sections) {
    if (sections.length === 0)
        return undefined;
    return (sections[sections.length - 2] ?? sections[sections.length - 1]).ref;
}
/**
 * Resolve a section's effective anchor ref against the declared sections:
 *   - `afterRef` set + still valid → that ref;
 *   - `afterRef` unset (new / migrated section) → the default placement
 *     (before the final declared section);
 *   - `afterRef` set but STALE (the named ref no longer exists, e.g. a
 *     template was changed) → the LAST declared section, so the orphaned
 *     content is dumped at the very end rather than lost or silently moved.
 */
export function resolveCustomAfterRef(section, sections) {
    if (sections.length === 0)
        return undefined;
    if (section.afterRef === undefined)
        return defaultCustomAfterRef(sections);
    if (sections.some((s) => s.ref === section.afterRef))
        return section.afterRef;
    return sections[sections.length - 1].ref;
}
/**
 * Group custom sections by their resolved anchor ref, preserving array order
 * within each group. Used by both the numbering computation and the render
 * context so the document and the § numbers agree.
 */
export function groupCustomsByAfterRef(customs, sections) {
    const map = new Map();
    for (const c of customs) {
        const ref = resolveCustomAfterRef(c, sections);
        if (!ref)
            continue;
        const list = map.get(ref) ?? [];
        list.push(c);
        map.set(ref, list);
    }
    return map;
}
/**
 * Expand a template's `defaultCustomSections` seed into a `CustomSection[]`
 * with fresh ids (generated by the caller — keeps this package crypto-free).
 * A seed with no sub-sections gets one blended sub-section holding an empty
 * free-text component, so the user lands on a ready-to-type box.
 */
export function seedCustomSections(seeds, genId) {
    if (!seeds?.length)
        return [];
    return seeds.map((s) => ({
        id: genId(),
        title: s.title,
        ...(s.afterRef ? { afterRef: s.afterRef } : {}),
        subSections: (s.subSections ?? [{ components: [{ kind: 'text' }] }]).map((ss) => ({
            id: genId(),
            ...(ss.title ? { title: ss.title } : {}),
            components: (ss.components ?? []).map((c) => c.kind === 'text'
                ? { id: genId(), kind: 'text', body: c.body ?? '' }
                : {
                    id: genId(),
                    kind: 'key_value',
                    rows: [...(c.rows ?? [{ key: '', value: '' }])],
                }),
        })),
    }));
}
// ── Template capability ───────────────────────────────────────────────────
/**
 * Whether a template offers custom sections. True for every section-model
 * template (declares `metadata.sections`) that is single-document. Multi-
 * document templates (ADR-0057) split their sections across independent
 * per-part numbering providers, so placement can't be routed coherently —
 * they opt out. Editor gates on this.
 */
export function templateSupportsCustomSections(mod) {
    if ((mod.documents?.length ?? 0) > 0)
        return false;
    // Framed templates allow customs between declared sections; frame-less
    // ones (clean bilaga, per ADR-0061) ARE custom sections.
    return ((mod.metadata.sections?.length ?? 0) > 0 || !!mod.metadata.customSectionsBody);
}
//# sourceMappingURL=custom-sections.js.map